import HintTooltip from "components/Ui/HintTooltip/HintTooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";

/**
 * Default `<thead>` for a configurable table.
 *
 * For columns whose header needs custom content (e.g. a select-all checkbox),
 * pass `headerRenderers` keyed by column id.
 *
 * SORTING IS OPT-IN PER COLUMN, and the list of which columns opt in comes from
 * the caller rather than from this component. A header that can be clicked has
 * to be one the SERVER can order by — the tables are paginated, so sorting the
 * ten rows on screen would reorder a page rather than the list, which is worse
 * than not sorting at all. `sortableColumns` is therefore the API's allow-list,
 * spelled the same way, and anything outside it draws as a plain header.
 *
 * The icon always shows on a sortable column, greyed until it is the active
 * one. A control that appears only on hover cannot be found by someone who does
 * not already know it is there.
 */
/**
 * `wrap: true` lets a long header break onto a second line. `table.scss` sets
 * `white-space: nowrap` on every cell, so a long label would otherwise set the
 * column's width by itself. `hint` on a column becomes the header's tooltip: a
 * right-to-left HintTooltip, not the browser's `title`, which wraps Arabic as
 * left-to-right text.
 */
const headerStyle = (col) =>
    // `minWidth` too: a cell that may wrap is the first one the browser
    // shrinks, and a long label then breaks into five lines instead of two.
    col.wrap
        ? { width: col.width, minWidth: col.width, whiteSpace: "normal" }
        : { width: col.width };

const TableHead = ({ visibleColumns, headerRenderers, sortableColumns, sort, onSortChange }) => {
    const sortable = new Set(sortableColumns || []);

    const iconFor = (columnId) => {
        if (sort?.by !== columnId) return faSort;
        return sort.dir === "desc" ? faSortDown : faSortUp;
    };

    return (
        <thead>
            <tr>
                {visibleColumns.map((col) => {
                    const render = headerRenderers?.[col.id];
                    const canSort = sortable.has(col.id) && typeof onSortChange === "function";

                    const withHint = (node) =>
                        col.hint ? <HintTooltip lines={[col.hint]}>{node}</HintTooltip> : node;

                    if (render || !canSort) {
                        return (
                            <th key={col.id} style={headerStyle(col)}>
                                {withHint(<span>{render ? render() : col.label}</span>)}
                            </th>
                        );
                    }

                    const isActive = sort?.by === col.id;

                    return (
                        <th key={col.id} style={headerStyle(col)}>
                            {withHint(
                                <button
                                    type="button"
                                    className={`th-sort${isActive ? " th-sort--active" : ""}`}
                                    // First click sorts ascending; clicking the column
                                    // that is already active flips it. Two clicks
                                    // therefore always reach descending, which is what
                                    // an operator is usually after on a count or a date.
                                    onClick={() =>
                                        onSortChange(
                                            col.id,
                                            isActive && sort.dir === "asc" ? "desc" : "asc",
                                        )
                                    }
                                    aria-label={`ترتيب حسب ${col.label}`}
                                >
                                    <span>{col.label}</span>
                                    <FontAwesomeIcon icon={iconFor(col.id)} size="sm" />
                                </button>,
                            )}
                        </th>
                    );
                })}
            </tr>
        </thead>
    );
};

export default TableHead;
